import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Grid2,
  Typography,
  CircularProgress,
  Button,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";

const Step1 = ({ rfq, setRfq, setStep }) => {
  const { id } = useParams();
  const [btnLoading, setBtnLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const palletOptions = Array.from({ length: 50 }, (_, i) =>
    (i + 1).toString(),
  );
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    if (id) {
      const fetchDraft = async () => {
        try {
          setPageLoading(true);
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/logistic-draft/get-by-id?logisticDraftId=${id}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );
          if (res.data) setRfq(res.data);
        } catch (error) {
          console.error("Fetch error:", error);
        } finally {
          setPageLoading(false);
        }
      };
      fetchDraft();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRfq((prev) => ({ ...prev, [name]: value }));
  };

  const autocompleteStyles = {
    marginTop: "5px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px",
      minHeight: "48px",
      padding: "0 !important",
      fontFamily: "Inter !important",
      "& fieldset": { borderColor: "#c7c7c7" },
      "&:hover fieldset": { borderColor: "#D8E9F0" },
      "&.Mui-focused fieldset": { borderColor: "#1a71f6", borderWidth: "1px" },
    },
    "& .MuiOutlinedInput-input": { padding: "12px 15px !important" },
  };

  const autocompletePaperProps = {
    sx: {
      background: "#D8E9F0",
      marginTop: "5px",
      "& .MuiAutocomplete-option": {
        fontSize: "14px",
        fontFamily: "Inter",
        justifyContent: "center",
        borderBottom: "1px solid #ffffff",
      },
    },
  };

  useEffect(() => {
    const L = parseFloat(rfq.length) || 0;
    const B = parseFloat(rfq.breadth) || 0;
    const H = parseFloat(rfq.height) || 0;

    if (L > 0 && B > 0 && H > 0) {
      const unitCbm = (L * B * H)/61024;
      const calculatedCbm = unitCbm.toFixed(4);
      if (String(rfq.volumeCBM) !== calculatedCbm) {
        setRfq((prev) => ({ ...prev, volumeCBM: calculatedCbm }));
      }
    } else if (rfq.volumeCBM !== "0.000") {
      setRfq((prev) => ({ ...prev, volumeCBM: "0.000" }));
    }
  }, [rfq.length, rfq.breadth, rfq.height]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!storedUser.username) {
      alert("Session expired. Please log in again.");
      return;
    }

    setBtnLoading(true);

    const { currencies, workItems, ...step1Fields } = rfq;

    const payload = {
      ...step1Fields,
      username: storedUser.username,
    };

    console.log("Step1 payload:", JSON.stringify(payload, null, 2));

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/logistic-draft`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );

      if (res.data) {
        setRfq((prev) => ({
          ...prev,
          ...res.data,
          currencies: prev.currencies || res.data.currencies || [],
        }));
      }
      setStep(2);
    } catch (error) {
      console.error("Full error:", error);
      const backendMessage = error.response?.data;
      alert(
        `Error ${error.response?.status || ""}: ${
          typeof backendMessage === "string"
            ? backendMessage
            : backendMessage?.message || "Check backend connection."
        }`,
      );
    } finally {
      setBtnLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="dashboard-container">
      <Typography
        variant="h6"
        className="form-title"
        sx={{ paddingLeft: "20px", fontWeight: "bold" }}
      >
        Step 1: Logistics Details {id ? `(Editing #${id})` : ""}
      </Typography>

      <form onSubmit={handleSubmit} style={{ width: "100%", padding: "20px" }}>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>Mode of Consignment*</label>
            <select
              name="modeOfConsignment"
              required
              value={rfq.modeOfConsignment || ""}
              onChange={handleChange}
            >
              <option value="">--Select Mode--</option>
              <option value="Road">Road</option>
              <option value="Air">Air</option>
              <option value="Sea">Sea</option>
            </select>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>Consignee*</label>
            <input
              name="consignee"
              type="text"
              placeholder="Enter Consignee Name"
              required
              value={rfq.consignee || ""}
              onChange={handleChange}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <label htmlFor="submissionDate">Quotes required by date*</label>
            <input
              type="datetime-local"
              name="submissionDate"
              id="submissionDate"
              onChange={handleChange}
              value={rfq && rfq.submissionDate}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>Invoice No</label>
            <Autocomplete
              freeSolo
              options={[]}
              value={rfq.invoiceNo || ""}
              onInputChange={(event, newInputValue) =>
                setRfq((prev) => ({ ...prev, invoiceNo: newInputValue }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or Type Invoice..."
                  variant="outlined"
                  size="small"
                  sx={autocompleteStyles}
                />
              )}
              slotProps={{ paper: autocompletePaperProps }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>Transit Time *</label>
            <Autocomplete
              freeSolo
              options={palletOptions}
              value={rfq.transitTime || ""}
              onInputChange={(event, newInputValue) =>
                setRfq((prev) => ({ ...prev, transitTime: newInputValue }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or Type Days..."
                  variant="outlined"
                  size="small"
                  required
                  sx={autocompleteStyles}
                />
              )}
              slotProps={{ paper: autocompletePaperProps }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>No of pallets*</label>
            <Autocomplete
              freeSolo
              options={palletOptions}
              value={rfq.noOfPallets || ""}
              onInputChange={(event, newInputValue) =>
                setRfq((prev) => ({ ...prev, noOfPallets: newInputValue }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select or Type..."
                  variant="outlined"
                  size="small"
                  required
                  sx={autocompleteStyles}
                />
              )}
              slotProps={{ paper: autocompletePaperProps }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>Gross Weight (Kg)*</label>
            <input
              name="grossWeight"
              type="number"
              placeholder="Enter Weight"
              required
              value={rfq.grossWeight || ""}
              onChange={handleChange}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 8 }}>
            <label>Dimensions (L * B * H) in inches*</label>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                name="length"
                type="number"
                placeholder="L"
                required
                value={rfq.length || ""}
                onChange={handleChange}
              />
              <span style={{ fontWeight: "bold", marginBottom: "20px" }}>
                *
              </span>
              <input
                name="breadth"
                type="number"
                placeholder="B"
                required
                value={rfq.breadth || ""}
                onChange={handleChange}
              />
              <span style={{ fontWeight: "bold", marginBottom: "20px" }}>
                *
              </span>
              <input
                name="height"
                type="number"
                placeholder="H"
                required
                value={rfq.height || ""}
                onChange={handleChange}
              />
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <label>Total Volume (CBM)</label>
            <input
              name="volumeCBM"
              readOnly
              style={{
                backgroundColor: "#D8E9F0",
                cursor: "not-allowed",
                fontWeight: "bold",
              }}
              value={rfq.volumeCBM || "0.000"}
            />
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <label>Address Details*</label>
            <textarea
              name="address"
              placeholder="Pickup & Delivery Address"
              rows="2"
              required
              value={rfq.address || ""}
              onChange={handleChange}
            />
          </Grid2>
        </Grid2>

        <Box className="btn-container" sx={{ gap: 3 }}>
          <Button className="btn-contained" type="submit" disabled={btnLoading}>
            {btnLoading ? "Saving..." : "Next"}
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default Step1;
